import { useState } from "react";
import { isAxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  skillConfigSchema,
  type Skill,
  type SkillConfig,
  type SkillScope,
} from "@/lib/data/schemas/skills";
import { saveSkill } from "@/lib/data/requests/skills";
import { skillsQueryKey } from "@/lib/data/react-query/skills";
import { automationError } from "@/components/features/automations/automationError";
export function SkillEditor({
  skill,
  initialScope,
  scopes,
  onClose,
}: {
  skill?: Skill;
  initialScope: SkillScope;
  scopes: SkillScope[];
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<SkillConfig>(
    () =>
      skill ?? {
        scope: initialScope,
        name: "",
        description: "",
        instructions: "",
        enabled: true,
      },
  );
  const [validation, setValidation] = useState<string>();
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: (config: SkillConfig) =>
      saveSkill(config, skill?.id, skill?.revision),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: skillsQueryKey });
      onClose();
    },
  });
  const nameConflict = automationError(save.error).includes(
    "skill with this name already exists",
  );
  const conflict =
    Boolean(skill) &&
    isAxiosError(save.error) &&
    save.error.response?.status === 409 &&
    !nameConflict;
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        const parsed = skillConfigSchema.safeParse(draft);
        if (!parsed.success) {
          setValidation(
            "Enter a name, description, and instructions within the indicated limits.",
          );
          return;
        }
        setValidation(undefined);
        save.mutate(parsed.data);
      }}
    >
      {!skill && (
        <label className="block text-sm">
          Who can use this skill?
          <select
            aria-label="Skill ownership"
            className="bg-background mt-2 block w-full rounded-md border p-2"
            value={draft.scope}
            onChange={(event) => {
              setDraft({ ...draft, scope: event.target.value as SkillScope });
            }}
          >
            {scopes.map((scope) => (
              <option key={scope} value={scope}>
                {scope === "firm" ? "Everyone in the firm" : "Only me"}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="block text-sm">
        Name
        <Input
          className="mt-2"
          value={draft.name}
          maxLength={100}
          onChange={(event) => {
            setDraft({ ...draft, name: event.target.value });
          }}
          placeholder="Review a medical record"
          required
        />
      </label>
      <label className="block text-sm">
        When to use
        <textarea
          className="bg-background mt-2 block min-h-20 w-full rounded-md border p-2"
          value={draft.description}
          maxLength={1024}
          onChange={(event) => {
            setDraft({ ...draft, description: event.target.value });
          }}
          required
        />
      </label>
      <label className="block text-sm">
        Instructions
        <textarea
          className="bg-background mt-2 block min-h-60 w-full rounded-md border p-3 font-mono text-sm"
          value={draft.instructions}
          maxLength={64000}
          onChange={(event) => {
            setDraft({ ...draft, instructions: event.target.value });
          }}
          required
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(event) => {
            setDraft({ ...draft, enabled: event.target.checked });
          }}
        />
        Enabled
      </label>
      {(validation || save.error) && (
        <p role="alert" className="text-destructive text-sm">
          {validation ||
            (conflict
              ? "Someone changed this skill while you were editing. Your changes have not been saved. Copy your changes, then reopen the skill to review the latest version."
              : automationError(save.error))}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={save.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending || conflict}>
          {save.isPending ? "Saving…" : "Save skill"}
        </Button>
      </div>
    </form>
  );
}
